/**
 * THE PRE-RECORDED GREETING, PLAYED IN THE BROWSER.
 *
 * When the session-create response carries `greetingClip`, the call opens with
 * the agent's recording instead of the model speaking its greeting. This class
 * owns that one clip for one call, and every rule it follows is about one of
 * two failures: a greeting that plays over the model, and a call that opens in
 * silence.
 *
 * IT PLAYS AS SOON AS IT CAN, NOT WHEN THE CALL CONNECTS. The clip is local
 * audio, so it needs nothing from the provider: it starts the moment its bytes
 * are decoded and the AudioContext exists, while the connection comes up in
 * the background. The caller hears the greeting sooner than any model could
 * speak one, and connecting in parallel means there is no dead air after it.
 *
 * THE CALLER'S MIC IS HELD SILENT FROM THE START UNTIL THE CLIP ENDS. Audio
 * played from a file is not something the browser's echo canceller knows
 * about, so with speakers on, the greeting would leak back into the mic and
 * reach the model as if the caller had said it. Held silent — the track
 * disabled, which still sends silence so the provider's clock keeps running —
 * the model hears neither the clip nor the room. That is also what removes
 * the noisy-room problem: nothing is there to make the model wait. The hold
 * starts at construction, before the peer connection exists, so not one frame
 * of the clip can go out.
 *
 * ONE DECISION, REPORTED ONCE. The DO needs to know whether to tell the model
 * "the caller heard the greeting" or to fall back to the spoken greeting, and
 * it must never be told both. So the clip either reaches `started` (and may
 * later report `ended`), or it reaches `failed` — could not fetch, could not
 * decode, or not playing within the deadline — and after `failed` it NEVER
 * plays, even if the bytes arrive a moment later. A late greeting would talk
 * over the spoken one the DO has just asked for.
 */

export interface ClipPlayback {
  durationMs: number
  /** Resolves when playback finishes or is stopped. */
  ended: Promise<void>
  stop(): void
}

export interface GreetingClipReport {
  event: 'started' | 'ended' | 'failed'
  reason?: string
  durationMs?: number
}

export interface GreetingClipDeps {
  /** The clip's WAV bytes. */
  fetch(): Promise<ArrayBuffer>
  /** Decode and start playing. Rejects if the audio cannot be decoded. */
  play(bytes: ArrayBuffer): Promise<ClipPlayback>
  /** Hold the caller's mic silent (true) or release the hold (false). */
  holdMic(held: boolean): void
  /** Tell the server. Called at most once with `started`/`failed`. */
  report(msg: GreetingClipReport): void
  /** The greeting is audible (true) or not (false), for the speaking indicator. */
  onPlaying(playing: boolean): void
  setTimer(fn: () => void, ms: number): unknown
  clearTimer(handle: unknown): void
  now(): number
}

/**
 * How long, from the session being created, the clip may take to start. The
 * DO waits longer than this for a report (CLIP_REPORT_WAIT_MS in
 * session-do.ts), so the browser's own decision always arrives first.
 */
export const GREETING_CLIP_DEADLINE_MS = 4_000

type State = 'loading' | 'decoding' | 'playing' | 'done' | 'failed' | 'stopped'

export class GreetingClip {
  private state: State = 'loading'
  private bytes: ArrayBuffer | null = null
  private audioIsReady = false
  private playback: ClipPlayback | null = null
  private startedAt = 0
  private readonly deadline: unknown

  constructor(private readonly deps: GreetingClipDeps, deadlineMs = GREETING_CLIP_DEADLINE_MS) {
    deps.holdMic(true)
    this.deadline = deps.setTimer(() => this.fail('not_ready'), deadlineMs)
    deps.fetch().then(
      (bytes) => {
        this.bytes = bytes
        this.maybePlay()
      },
      () => this.fail('fetch_failed'),
    )
  }

  /** The AudioContext exists and is running, so the clip can be heard. */
  audioReady(): void {
    this.audioIsReady = true
    this.maybePlay()
  }

  /** There will be no AudioContext on this call. */
  audioUnavailable(): void {
    this.fail('no_audio_output')
  }

  get isPlaying(): boolean {
    return this.state === 'playing'
  }

  /** The call is ending. Silence any audio and report nothing more. */
  stop(): void {
    const was = this.state
    if (was === 'stopped') return
    this.state = 'stopped'
    this.deps.clearTimer(this.deadline)
    if (was === 'playing') {
      this.playback?.stop()
      this.deps.onPlaying(false)
    }
    this.playback = null
    this.deps.holdMic(false)
  }

  private maybePlay(): void {
    if (this.state !== 'loading' || this.bytes === null || !this.audioIsReady) return
    this.state = 'decoding'
    const bytes = this.bytes
    this.bytes = null
    this.deps.play(bytes).then(
      (playback) => {
        // The deadline passed, or the call ended, while decoding: too late.
        if (this.state !== 'decoding') {
          playback.stop()
          return
        }
        this.deps.clearTimer(this.deadline)
        this.state = 'playing'
        this.playback = playback
        this.startedAt = this.deps.now()
        this.deps.onPlaying(true)
        this.deps.report({ event: 'started', durationMs: playback.durationMs })
        void playback.ended.then(() => this.finish())
      },
      () => this.fail('decode_failed'),
    )
  }

  private finish(): void {
    if (this.state !== 'playing') return
    this.state = 'done'
    this.playback = null
    this.deps.onPlaying(false)
    this.deps.holdMic(false)
    this.deps.report({ event: 'ended', durationMs: Math.max(0, this.deps.now() - this.startedAt) })
  }

  private fail(reason: string): void {
    if (this.state !== 'loading' && this.state !== 'decoding') return
    this.state = 'failed'
    this.bytes = null
    this.deps.clearTimer(this.deadline)
    this.deps.holdMic(false)
    this.deps.report({ event: 'failed', reason })
  }
}
